import React, { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery } from "@tanstack/react-query";
import { PaziaModal } from "components/molecules";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaButton, PaziaFormButton, PaziaInput, PaziaSelect, IDropdownOption } from "components/atoms";
import { useTrainers } from "api/hooks";
import { formatDate, toMoneyFormat } from "utils";
import { courseTrainersApi } from "api/courseTrainersApi";
import {
  COURSE_TRAINER_COST_SCOPE,
  CourseTrainerCostEntryApi,
  TRAINER_RATE_TYPE,
  UpdateTrainerCostEntryRequest,
} from "api/trainer-costs/trainerCostsApi";
import {
  buildRateOptions,
  calculateCost,
  isManualRateSelection,
  MANUAL_RATE_OPTIONS,
  resolveRateType,
} from "./trainerRateCalculation";

const FIELDS = { RATE_SELECTION: "rateSelection", RATE_AMOUNT: "rateAmount" } as const;
const schema = yup.object({
  [FIELDS.RATE_SELECTION]: yup.string().required(),
  [FIELDS.RATE_AMOUNT]: yup.number().required().moreThan(0),
});
type FormValues = yup.InferType<typeof schema>;

interface EditTrainerCostEntryModalProps {
  isOpen: boolean;
  onCloseRequest: () => void;
  courseId: number;
  entry: CourseTrainerCostEntryApi | null;
  onSubmit: (data: UpdateTrainerCostEntryRequest) => Promise<void>;
}
export const EditTrainerCostEntryModal = ({
  isOpen,
  onCloseRequest,
  courseId,
  entry,
  onSubmit,
}: EditTrainerCostEntryModalProps) => {
  const { trainers: allTrainers } = useTrainers(0, 1_000_000);
  const { data } = useQuery({
    queryKey: ["fetchTrainersByCourseId", courseId],
    queryFn: () => courseTrainersApi.fetchByCourseId(courseId),
  });

  const fullTrainer = allTrainers.find((x) => x.id === entry?.trainerId);
  const courseTrainer = data?.data.find((x) => x.id === entry?.trainerId);

  const methods = useForm<FormValues>({ resolver: yupResolver(schema) });
  const { watch, handleSubmit, setValue, reset } = methods;

  useEffect(() => {
    if (!entry) return;
    const matchedIndex = entry.rateSourceName
      ? (fullTrainer?.prices?.findIndex((p) => p.name === entry.rateSourceName) ?? -1)
      : -1;
    const initialRateSelection =
      matchedIndex >= 0
        ? matchedIndex.toString()
        : entry.rateType === TRAINER_RATE_TYPE.HOURLY
          ? MANUAL_RATE_OPTIONS.HOURLY
          : entry.rateType === TRAINER_RATE_TYPE.DAILY
            ? MANUAL_RATE_OPTIONS.DAILY
            : MANUAL_RATE_OPTIONS.TOPIC;
    reset({ rateSelection: initialRateSelection, rateAmount: entry.rateAmount });
    // Intentionally keyed on ids rather than object identity: fullTrainer is a fresh object on
    // every trainers-list refetch (bare QueryClient defaults to refetchOnWindowFocus/staleTime 0),
    // which must not re-run this effect and clobber an in-progress edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.id, fullTrainer?.id, reset]);

  const rateSelection = watch(FIELDS.RATE_SELECTION);
  const rateAmount = watch(FIELDS.RATE_AMOUNT);

  const rateOptions: IDropdownOption[] = buildRateOptions(fullTrainer);
  const isManualRate = isManualRateSelection(rateSelection ?? "");

  const scopedPrices = useMemo(() => {
    if (!entry || !courseTrainer) return [];
    const prices = courseTrainer.prices;
    if (entry.scopeType === COURSE_TRAINER_COST_SCOPE.SESSION)
      return prices.filter((p) => p.courseSessionId === entry.courseSessionId);
    if (entry.scopeType === COURSE_TRAINER_COST_SCOPE.DAY)
      return prices.filter(
        (p) => formatDate(p.date, false, false, "short") === formatDate(entry.date!, false, false, "short"),
      );
    return prices;
  }, [entry, courseTrainer]);

  const preview =
    rateSelection && rateAmount
      ? calculateCost(scopedPrices, resolveRateType(rateSelection, fullTrainer), Number(rateAmount))
      : null;

  const onSubmitHandler = async (data: FormValues) => {
    await onSubmit({
      rateType: resolveRateType(data.rateSelection, fullTrainer),
      rateAmount: Number(data.rateAmount),
      rateSourceName: isManualRate ? undefined : fullTrainer?.prices?.[Number(data.rateSelection)]?.name,
    });
  };

  return (
    // The modal must stay mounted while closed: Transition only clears its initial `display: none` in
    // onEnter, which never fires when it mounts already open — returning null without an entry made
    // the pencil click render an invisible modal.
    <PaziaModal id="edit-trainer-cost-entry-modal" isOpen={isOpen && !!entry} onCloseRequest={onCloseRequest} size="lg">
      {entry && (
        <ModalPadding>
          <div className="mb-2 text-xl font-medium text-slate-800 dark:text-slate-100">Edytuj wpis kosztu</div>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmitHandler)} className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <PaziaSelect
                  name={FIELDS.RATE_SELECTION}
                  label="Stawka"
                  options={rateOptions}
                  required
                  isClearable={false}
                  onChange={(opt: IDropdownOption) => {
                    if (!isManualRateSelection(opt.value.toString()) && fullTrainer) {
                      setValue(FIELDS.RATE_AMOUNT, fullTrainer.prices[Number(opt.value)].amount);
                    }
                  }}
                />
              </div>
              <div className="col-span-2">
                <PaziaInput name={FIELDS.RATE_AMOUNT} label="Kwota" type="number" step="0.01" required disabled={!isManualRate} />
              </div>
              {preview && (
                <div className="col-span-2 text-end text-sm text-primary-700 dark:text-primary-200">
                  {preview.quantity !== null && <>Ilość: {preview.quantity.toFixed(2)} &mdash; </>}
                  Koszt: <span className="font-semibold">{toMoneyFormat(preview.total)} PLN</span>
                </div>
              )}
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <PaziaButton title="Anuluj" type="tertiary" btnType="button" onClick={onCloseRequest} />
                {/* The form opens already filled (reset to the entry), so it is never "dirty" until the
                  rate select changes — and for a catalog rate the amount input is locked. Without
                  allowCleanSubmit the button stayed greyed out and the pencil looked broken. */}
                <PaziaFormButton title="Zapisz" type="success" allowCleanSubmit />
              </div>
            </form>
          </FormProvider>
        </ModalPadding>
      )}
    </PaziaModal>
  );
};
